import { downloadJsonFile } from './atlasDownloadHelpers'
import {
    fetchActivitiesForExport,
    buildActivityById,
    buildEvidenceById,
    fetchValueMessagesForExport,
    fetchEvidenceForExport,
    buildValueMessageNameMap,
    fetchValueNarrativesForExport,
    fetchStrategicImperativesForExport,
    buildStrategicImperativesByActivityId,
} from './atlasExportDataFetchers'

import {
    transformLiveTppDataForExport,
    enrichTppDataForExecSummary,
    normalizeTppForStandardExport,
} from './pptTPPhelpers'
import { getOrderedValueMessages } from './pptExportHelpers'

export function cleanExportDate(value) {
    if (!value) return null

    try {
        return new Date(value).toISOString().split('T')[0]
    } catch {
        return null
    }
}

const safeId = (x) => {
    if (x == null) return ''
    if (typeof x === 'object') return String(x._id || x.id || '')
    return String(x)
}

const asIdList = (arr) => (Array.isArray(arr) ? arr.map(safeId).filter(Boolean) : [])

export function safeExportFileName(name = 'export') {
    return String(name)
        .replace(/[<>:"/\\|?*()]/g, '') // remove invalid filename chars + parentheses
        .replace(/\s+/g, '_') // spaces -> underscores
        .trim()
}

function downloadActivitiesJson(
    activitiesArray,
    vmNameMap = {},
    strategicImperativesByActivityId = {},
    strategicDisplayName = 'Strategic Imperatives',
    evidenceById = {},
    activityById = {}
) {
    const rows = Array.isArray(activitiesArray) ? activitiesArray : []

    const resolveEvidenceRefs = (refs = []) =>
        (Array.isArray(refs) ? refs : [])
            .map(safeId)
            .map((id) => evidenceById[id])
            .filter(Boolean)
            .map((ev) => ({
                title: ev?.name ?? '',
                citation: ev?.citation ?? '',
                link: ev?.link ?? '',
            }))

    const resolveActivityRefs = (refs = []) =>
        (Array.isArray(refs) ? refs : [])
            .map(safeId)
            .map((id) => activityById[id])
            .filter(Boolean)
            .map((act) => ({
                title: act?.title ?? '',
                status: act?.status ?? '',
                startDate: cleanExportDate(act?.startDate),
                endDate: cleanExportDate(act?.endDate),
            }))

    const data = rows.map((a) => {
        const vmIds = Array.isArray(a?.linkedValueMessages) ? a.linkedValueMessages : []
        const vmTexts = vmIds.map((id) => vmNameMap[id] || `(Missing VM: ${id})`)

        return {
            studyNumber: a?.studyNumber ?? '',
            title: a?.title ?? '',
            description: a?.description ?? '',
            anticipatedMessaging: a?.anticipatedMessaging ?? '',
            dataSource: a?.dataSource ?? '',
            category: a?.category ?? '',
            function: a?.function ?? '',
            status: a?.status ?? '',
            studyType: a?.studyType ?? '',
            proactiveReactive: a?.proactiveReactive ?? '',
            type: a?.type ?? '',
            startDate: cleanExportDate(a?.startDate),
            endDate: cleanExportDate(a?.endDate),
            intendedUse: a?.intendedUse ?? null,
            publication: a?.publication ?? null,
            outcomesEndpoints: a?.outcomesEndpoints ?? [],
            linkedEvidence: resolveEvidenceRefs(a?.linkedEvidence),
            [strategicDisplayName]: strategicImperativesByActivityId[String(a?._id || a?.id)] || [],
            activityDependencies: resolveActivityRefs(a?.activityDependencies),
            primaryEvidence: resolveEvidenceRefs(a?.primaryEvidence),
            linkedValueMessageTexts: vmTexts,
        }
    })

    downloadJsonFile(data, 'activities.json')
}

function downloadEvidenceJson(evidenceArray, { vmNameMap = {}, activityById = {}, kesByEvidenceId = new Map() } = {}) {
    const rows = Array.isArray(evidenceArray) ? evidenceArray : []

    const data = rows.map((ev) => {
        const evidenceId = ev?._id || ev?.id

        const vmIds = asIdList(ev?.linkedValueMessages)
        const linkedValueMessagesResolved = vmIds.map((id) => {
            const key = String(id)

            return {
                text: vmNameMap?.[key] ? vmNameMap[key] : `(Missing VM: ${key})`,
            }
        })

        const actIds = asIdList(ev?.linkedActivities)
        const linkedActivitiesResolved = actIds.map((id) => {
            const a = activityById ? activityById[id] : null

            if (a && typeof a === 'object') {
                return {
                    title: a?.title ?? '',
                    status: a?.status ?? '',
                    category: a?.category ?? '',
                    function: a?.function ?? '',
                    startDate: cleanExportDate(a?.startDate),
                    endDate: cleanExportDate(a?.endDate),
                }
            }

            return { id, title: `(Missing Activity: ${id})` }
        })

        const kesStrings =
            evidenceId && kesByEvidenceId && typeof kesByEvidenceId.get === 'function'
                ? kesByEvidenceId.get(evidenceId) || []
                : []

        return {
            filename: ev?.originalFileName ?? '',
            title: ev?.name ?? '',
            url: ev?.link ?? '',
            citation: ev?.citation ?? '',
            abstract: ev?.abstract ?? '',
            category: ev?.category ?? '',
            year: ev?.year ?? null,
            isInternal: ev?.isInternal ?? ev?.internal ?? false,
            author: ev?.author ?? '',
            uniqueStudyIdentifier: ev?.uniqueStudyIdentifier ?? '',
            subPopulation: ev?.subPopulation ?? '',
            population: ev?.population ?? '',
            nickname: ev?.nickname ?? '',
            pubmedId: ev?.pubmedId ?? null,
            publicationType: ev?.publicationType ?? '',
            uniqueIdentifier: ev?.uniqueIdentifier ?? '',
            linkedActivitiesResolved,
            linkedValueMessagesResolved,
            keyEvidenceStatements: Array.isArray(kesStrings) ? kesStrings : [],
        }
    })

    downloadJsonFile(data, 'evidence_full.json')
}

// export function downloadSlimKesJson(rows) {
//     const payload = Array.isArray(rows) ? rows : []
//     downloadJsonFile(payload, 'evidence_kes_citation.json')
// }

function downloadValueNarrativesFullJson(
    valueNarratives,
    vnsDisplayName,
    vmsDisplayName,
    { activityById = {}, evidenceById = {}, gapsByVmId = {}, kesByEvidenceId = new Map() } = {}
) {
    const normId = (x) => {
        if (!x) return ''

        if (typeof x === 'object') {
            const v = x._id || x.id
            if (!v) return ''
            return String(v)
        }

        return String(x)
    }

    const getActivity = (maybeIdOrObj) => {
        if (maybeIdOrObj && typeof maybeIdOrObj === 'object') {
            return maybeIdOrObj
        }

        const id = normId(maybeIdOrObj)

        return id ? activityById[id] : undefined
    }

    const getEvidence = (maybeIdOrObj) => {
        if (maybeIdOrObj && typeof maybeIdOrObj === 'object') {
            return maybeIdOrObj
        }

        const id = normId(maybeIdOrObj)

        return id ? evidenceById[id] : undefined
    }

    const payload = []

    for (const category of valueNarratives || []) {
        const categoryObj = {
            category: category?.name ?? '',
            [vnsDisplayName]: [],
        }

        // Exclude archived Value Narratives before exporting.
        const activeNarratives = (category?.valueNarratives || []).filter((narrative) => {
            const archiveValue = narrative?.archived ?? narrative?.valueMessages?.[0]?.narrative?.archived

            return archiveValue == null
        })

        for (const narrative of activeNarratives) {
            const narrativeObj = {
                text: narrative?.name?.trim() || `(No ${String(vnsDisplayName).toLowerCase()})`,
                subpopulation: narrative?.subpopulation ?? '',
                [vmsDisplayName]: [],
            }

            const vms = getOrderedValueMessages(narrative?.valueMessages)

            if (!vms.length) {
                narrativeObj[vmsDisplayName].push({
                    text: `(No ${String(vmsDisplayName).toLowerCase()}s)`,
                    status: '',
                    priority: '',
                    description: '',
                    activities: [],
                    evidenceGaps: [],
                    linkedEvidence: [],
                    keyEvidenceStatementsByEvidence: [],
                })

                categoryObj[vnsDisplayName].push(narrativeObj)
                continue
            }

            for (const vm of vms) {
                const vmId = normId(vm?._id || vm?.id)
                const vmText = vm?.name?.trim() ? vm.name.trim() : `(No ${String(vmsDisplayName).toLowerCase()})`

                const actRefs = Array.isArray(vm?.linkedActivities) ? vm.linkedActivities : []

                const activities = actRefs
                    .map(getActivity)
                    .filter(Boolean)
                    .map((a) => ({
                        title: a?.title ?? '',
                        status: a?.status ?? '',
                        startDate: cleanExportDate(a?.startDate),
                        endDate: cleanExportDate(a?.endDate),
                        category: a?.category ?? '',
                        function: a?.function ?? '',
                        description: a?.description ?? '',
                        studyNumber: a?.studyNumber ?? '',
                        studyType: a?.studyType ?? '',
                        proactiveReactive: a?.proactiveReactive ?? '',
                        anticipatedMessaging: a?.anticipatedMessaging ?? '',
                    }))

                const gapRows = vmId ? gapsByVmId[vmId] || [] : []

                const evidenceGaps = gapRows.map((g) => ({
                    text: g?.text ?? '',
                    order: g?.order ?? null,
                    isOpen: !!g?.isOpen,
                    linkedActivities: (Array.isArray(g?.linkedActivities) ? g.linkedActivities : [])
                        .map(getActivity)
                        .filter(Boolean)
                        .map((a) => ({
                            title: a?.title ?? '',
                        })),
                }))

                const evRefs = Array.isArray(vm?.linkedEvidence) ? vm.linkedEvidence : []

                const linkedEvidenceResolved = evRefs
                    .map(getEvidence)
                    .filter(Boolean)
                    .map((ev) => ({
                        _internalEvidenceId: normId(ev?._id || ev?.id),
                        title: ev?.name ?? '',
                        citation: ev?.citation ?? '',
                        author: ev?.author ?? '',
                        link: ev?.link ?? '',
                        year: ev?.year ?? null,
                        category: ev?.category ?? '',
                        pubmedId: ev?.pubmedId ?? null,
                        abstract: ev?.abstract ?? '',
                        uniqueStudyIdentifier: ev?.uniqueStudyIdentifier ?? '',
                        subPopulation: ev?.subPopulation ?? '',
                        population: ev?.population ?? '',
                    }))

                const keyEvidenceStatementsByEvidence = linkedEvidenceResolved.map((ev) => {
                    const evid = ev._internalEvidenceId
                    const statements = evid ? kesByEvidenceId.get(evid) || [] : []

                    return {
                        citation: ev.citation || '(No citation)',
                        link: ev.link || '',
                        statements,
                    }
                })

                const linkedEvidence = linkedEvidenceResolved.map(({ _internalEvidenceId, ...rest }) => rest)

                narrativeObj[vmsDisplayName].push({
                    text: vmText,
                    status: vm?.status ?? '',
                    priority: vm?.priority ?? '',
                    order: vm?.order ?? null,
                    description: vm?.description ?? '',
                    activities,
                    evidenceGaps,
                    linkedEvidence,
                    keyEvidenceStatementsByEvidence,
                })
            }

            categoryObj[vnsDisplayName].push(narrativeObj)
        }

        payload.push(categoryObj)
    }

    const safeName = safeExportFileName(vnsDisplayName || 'value_narratives')

    downloadJsonFile(payload, `${safeName}.json`)
}

export function buildTrppCellLinkageMapFromCells(
    cells = [],
    { activityById = {}, evidenceById = {}, vmNameMap = {}, kesByEvidenceId = new Map() } = {}
) {
    const map = {}

    for (const cell of cells || []) {
        const cellId = safeId(cell?._id)
        if (!cellId) continue

        const activities = (cell.linkedActivities || [])
            .map(safeId)
            .map((id) => activityById[id])
            .filter(Boolean)
            .map((a) => ({
                title: a?.title ?? '',
                status: a?.status ?? '',
                objective: a?.description ?? '',
                studyNumber: a?.studyNumber ?? '',
                studyType: a?.studyType ?? '',
                function: a?.function ?? '',
                startDate: cleanExportDate(a?.startDate),
                endDate: cleanExportDate(a?.endDate),
            }))

        const evidence = (cell.linkedEvidence || [])
            .map(safeId)
            .map((id) => evidenceById[id])
            .filter(Boolean)
            .map((ev) => {
                const evId = safeId(ev?._id || ev?.id)

                return {
                    title: ev?.name ?? '',
                    citation: ev?.citation ?? '',
                    link: ev?.link ?? '',
                    keyEvidenceStatements: evId ? kesByEvidenceId.get(evId) || [] : [],
                }
            })

        const valueMessages = (cell.linkedValueMessages || []).map(safeId).map((id) => {
            const key = String(id)

            return {
                text: vmNameMap?.[key] || `(Missing Value Message: ${key})`,
            }
        })

        map[cellId] = {
            activities,
            evidence,
            valueMessages,
        }
    }

    return map
}

// TODO Display name for export but make sure we don't bring in the () because I don't believe that works for filenames
function downloadTrppJson(normalizedTrpp, tppDisplayName = 'TrPP', linkageMap = {}) {
    const payload = {
        exportType: `${tppDisplayName} JSON`,
        assetName: normalizedTrpp?.assetName || '',
        assetAmbition: normalizedTrpp?.assetAmbition || '',
        scenarios: (normalizedTrpp?.scenarios || []).map((scenario) => ({
            name: scenario.name,
            order: scenario.order,
        })),
        categories: (normalizedTrpp?.categories || []).map((category) => ({
            category: category.name,
            attributes: (category.attributes || []).map((attribute) => ({
                attribute: attribute.name,
                information: attribute.info || '',
                slots: (attribute.groups || []).map((slot, idx) => {
                    const cellsByScenario = {}

                    for (const scenario of normalizedTrpp.scenarios || []) {
                        const bullets = slot.bulletsByScenarioId?.[scenario.id] || []

                        cellsByScenario[scenario.name] = bullets.map((b) => {
                            const linkage = linkageMap?.[b.cellId] || {}

                            return {
                                text: b.text || '',
                                differentiating: !!b.isHighlighted,
                                evidence: linkage.evidence || [],
                                activities: linkage.activities || [],
                                value_messages: linkage.valueMessages || [],
                            }
                        })
                    }

                    return {
                        slotNumber: idx + 1,
                        context: slot.slotContext || '',
                        cellsByScenario,
                    }
                }),
            })),
        })),
    }

    const safeName = safeExportFileName(tppDisplayName || 'trpp')

    downloadJsonFile(payload, `${safeName}.json`)
}

export async function exportActivitiesJson({
    callSecureAPI,
    endpoints,
    strategyId,
    token,
    vmNameMap = {},
    strategicDisplayName = 'Strategic Imperatives',
}) {
    const [activities, strategicImperatives, valueMessages, evidence] = await Promise.all([
        fetchActivitiesForExport({ callSecureAPI, endpoints, strategyId, token }),
        fetchStrategicImperativesForExport({ callSecureAPI, endpoints, strategyId, token }),
        fetchValueMessagesForExport({ callSecureAPI, endpoints, strategyId, token }),
        fetchEvidenceForExport({ callSecureAPI, endpoints, strategyId, token }),
    ])

    const strategicImperativesByActivityId = buildStrategicImperativesByActivityId(strategicImperatives)

    const resolvedVmNameMap =
        vmNameMap && Object.keys(vmNameMap).length ? vmNameMap : buildValueMessageNameMap(valueMessages)

    const evidenceById = buildEvidenceById(evidence)

    const activityById = buildActivityById(activities)

    downloadActivitiesJson(
        activities,
        resolvedVmNameMap,
        strategicImperativesByActivityId,
        strategicDisplayName,
        evidenceById,
        activityById
    )
}

export async function exportTrppJson({
    callSecureAPI,
    endpoints,
    strategy,
    token,
    tppDisplayName = 'TrPP',
    activityById = {},
    evidenceById = {},
    vmNameMap = {},
    kesByEvidenceId = new Map(),
}) {
    const roadmapId = strategy?._id
    const assetName = `${strategy?.product || ''} for ${strategy?.indication || ''}`.trim()

    if (!roadmapId) throw new Error('No strategy selected')

    const tppIdRes = await callSecureAPI(`/api/tpps/roadmap/${roadmapId}`, 'get', token)

    if (!tppIdRes.success || !tppIdRes.data) {
        throw new Error('Could not retrieve TPP for this strategy')
    }

    const tppId = tppIdRes.data

    const [tppRes, columnsRes, sectionsRes, rowsRes, slotsRes, cellsRes] = await Promise.all([
        callSecureAPI(`/api/tpps/${tppId}`, 'get', token),
        callSecureAPI(`/api/tpps/columns/${tppId}`, 'get', token),
        callSecureAPI(`/api/tpps/sections/${tppId}`, 'get', token),
        callSecureAPI(`/api/tpps/rows/${tppId}`, 'get', token),
        callSecureAPI(`/api/tpps/slots/${tppId}`, 'get', token),
        callSecureAPI(`/api/tpps/cells/${tppId}`, 'get', token),
    ])

    if (!tppRes.success) throw new Error('Failed to fetch TPP data')

    let resolvedActivityById = activityById

    if (!resolvedActivityById || !Object.keys(resolvedActivityById).length) {
        const activities = await fetchActivitiesForExport({
            callSecureAPI,
            endpoints,
            strategyId: roadmapId,
            token,
        })

        resolvedActivityById = buildActivityById(activities)
    }

    let resolvedVmNameMap = vmNameMap

    if (!resolvedVmNameMap || !Object.keys(resolvedVmNameMap).length) {
        const valueMessages = await fetchValueMessagesForExport({
            callSecureAPI,
            endpoints,
            strategyId: roadmapId,
            token,
        })

        resolvedVmNameMap = buildValueMessageNameMap(valueMessages)
    }

    const trppData = transformLiveTppDataForExport({
        tpp: tppRes.data,
        columns: columnsRes.data || [],
        sections: sectionsRes.data || [],
        rows: rowsRes.data || [],
        slots: slotsRes.data || [],
        cells: cellsRes.data || [],
        assetName,
    })

    const enrichedTrppData = enrichTppDataForExecSummary(trppData, {
        tppRes,
        rows: rowsRes?.data || [],
        slots: slotsRes?.data || [],
    })

    const normalizedFull = normalizeTppForStandardExport(enrichedTrppData, {
        highlightedOnly: false,
        includeSlotContext: true,
        includeAttributeInfo: true,
    })

    const linkageMap = buildTrppCellLinkageMapFromCells(cellsRes?.data || [], {
        activityById: resolvedActivityById,
        evidenceById,
        vmNameMap: resolvedVmNameMap,
        kesByEvidenceId,
    })

    downloadTrppJson(normalizedFull, tppDisplayName, linkageMap)
}

export async function exportValueNarrativesFullJson({
    callSecureAPI,
    endpoints,
    strategyId,
    token,
    vnsDisplayName,
    vmsDisplayName,
    activityById = {},
    evidenceById = {},
    gapsByVmId = {},
    kesByEvidenceId = new Map(),
}) {
    const valueNarratives = await fetchValueNarrativesForExport({
        callSecureAPI,
        endpoints,
        strategyId,
        token,
    })

    downloadValueNarrativesFullJson(valueNarratives, vnsDisplayName, vmsDisplayName, {
        activityById,
        evidenceById,
        gapsByVmId,
        kesByEvidenceId,
    })
}

export async function exportEvidenceJson({
    callSecureAPI,
    endpoints,
    strategyId,
    token,
    vmNameMap = null,
    activityById = null,
    kesByEvidenceId = new Map(),
}) {
    const [evidence, activities, valueMessages] = await Promise.all([
        fetchEvidenceForExport({
            callSecureAPI,
            endpoints,
            strategyId,
            token,
        }),
        fetchActivitiesForExport({
            callSecureAPI,
            endpoints,
            strategyId,
            token,
        }),
        fetchValueMessagesForExport({
            callSecureAPI,
            endpoints,
            strategyId,
            token,
        }),
    ])

    const resolvedActivityById = activityById || buildActivityById(activities)
    const resolvedVmNameMap = vmNameMap || buildValueMessageNameMap(valueMessages)

    downloadEvidenceJson(evidence, {
        vmNameMap: resolvedVmNameMap,
        activityById: resolvedActivityById,
        kesByEvidenceId,
    })
}
